import React, { useState, useEffect } from 'react';
import { Card, CardBody, Typography, Input, Button, Checkbox } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaTimes, FaPenAlt, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { useAuth } from '../myTrack/AuthContext';
import { rootUrl } from '../../config/config';

const PayersManagement = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        code: '',
        name: '',
        description: '',
        sort_order: 0,
        is_active: true,
    });
    const { user } = useAuth();

    const token = localStorage.getItem('authToken') || '';

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/api/system/payers`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setItems(response.data.data || []);
            setError('');
        } catch (err) {
            setError('Failed to fetch payers.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchItems();
    }, []);

    const startEdit = (item) => {
        setEditingId(item.id);
        setForm({
            code: item.code || '',
            name: item.name || '',
            description: item.description || '',
            sort_order: item.sort_order || 0,
            is_active: !!item.is_active,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelForm = () => {
        setEditingId(null);
        setForm({ code: '', name: '', description: '', sort_order: 0, is_active: true });
        setError('');
    };

    const deleteItem = async (id) => {
        if (!window.confirm('Delete this payer?')) return;
        try {
            await axios.delete(`${rootUrl}/api/system/payers/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchItems();
        } catch (err) {
            setError('Failed to delete payer.');
            console.error(err);
        }
    };

    const toggleActive = async (item) => {
        try {
            await axios.patch(
                `${rootUrl}/api/system/payers/${item.id}/toggle`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchItems();
        } catch (err) {
            setError('Failed to toggle payer status.');
            console.error(err);
        }
    };

    const validateForm = () => {
        const code = form.code.trim();
        const name = form.name.trim();
        if (!code || !name) return 'Code and name are required.';
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) { setError(validationError); return; }

        try {
            const payload = {
                code: form.code.toUpperCase(),
                name: form.name,
                description: form.description || null,
                sort_order: parseInt(form.sort_order) || 0,
                ...(editingId ? { edit_by: user?.username || 'system' } : { input_by: user?.username || 'system' }),
            };

            if (editingId) {
                await axios.put(`${rootUrl}/api/system/payers/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                await axios.post(`${rootUrl}/api/system/payers`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            cancelForm();
            fetchItems();
        } catch (err) {
            const msg = err?.response?.data?.message || (editingId ? 'Failed to update payer.' : 'Failed to add payer.');
            setError(msg);
            console.error(err);
        }
    };

    return (
        <div className="w-full max-w-7xl mx-auto mt-6 px-2 sm:px-4">
            <Card className="border-t">
                <CardBody className="p-3 sm:p-6">
                    <h2 className="text-xl sm:text-2xl font-semibold text-gray-800 mb-4 sm:mb-6">Payers Management</h2>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
                    )}

                    <form onSubmit={handleSubmit} className="mb-6 space-y-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-700">{editingId ? 'Edit Payer' : 'Add New Payer'}</h3>
                            {editingId && (
                                <Button type="button" variant="outlined" color="gray" onClick={cancelForm} className="flex items-center shadow-none rounded-none">
                                    <FaTimes className="mr-2" /> Cancel
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <Input type="text" label="Code *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="uppercase" style={{ textTransform: 'uppercase' }} required />
                            <Input type="text" label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                            <Input type="text" label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                            <Input type="number" label="Sort Order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
                            <div className="flex items-end pb-2">
                                <Checkbox
                                    label="Active"
                                    checked={form.is_active}
                                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                    className="w-5 h-5"
                                    color="blue"
                                />
                            </div>
                        </div>

                        <Button type="submit" className="bg-brand-primary text-white flex items-center shadow-none rounded-none">
                            <FaPlus className="mr-2" /> {editingId ? 'Update Payer' : 'Add Payer'}
                        </Button>
                    </form>

                    {loading ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <div className="overflow-x-auto -mx-2 sm:mx-0">
                            <table className="w-full table-auto border-collapse min-w-[900px]">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">ID</th>
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Code</th>
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Name</th>
                                        <th className="border px-2 sm:px-4 py-2 text-left text-xs sm:text-sm">Description</th>
                                        <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Sort Order</th>
                                        <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Status</th>
                                        <th className="border px-2 sm:px-4 py-2 text-center text-xs sm:text-sm">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50">
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="text-xs sm:text-sm">{item.id}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="uppercase text-xs sm:text-sm font-semibold">{item.code}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="text-xs sm:text-sm">{item.name}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2"><Typography className="text-xs sm:text-sm">{item.description || '-'}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2 text-center"><Typography className="text-xs sm:text-sm">{item.sort_order}</Typography></td>
                                            <td className="border px-2 sm:px-4 py-2 text-center">
                                                <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-none text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                                            </td>
                                            <td className="border px-2 sm:px-4 py-2 text-center">
                                                <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
                                                    <Button onClick={() => startEdit(item)} className="bg-blue-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none" size="sm" disabled={editingId === item.id}>
                                                        <FaPenAlt className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                                    </Button>
                                                    <Button onClick={() => toggleActive(item)} className={`${item.is_active ? 'bg-gray-600' : 'bg-green-600'} text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none`} size="sm">
                                                        {item.is_active ? (<FaToggleOff className="w-3 h-3" />) : (<FaToggleOn className="w-3 h-3" />)}
                                                    </Button>
                                                    <Button onClick={() => deleteItem(item.id)} className="bg-red-600 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs shadow-none rounded-none" size="sm" disabled={editingId === item.id}>
                                                        <FaTrash className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default PayersManagement;
